import PublicLayout from '@/layouts/public-layout';
import { SeoHead } from '@/components/seo-head';
import { Facebook, Mail } from 'lucide-react';

export default function AboutUs() {
    return (
        <PublicLayout>
            <SeoHead
                title="About blog.brajti.com"
                description="Welcome to blog.brajti.com, your new home for discovering, discussing, and diving deep into the world of books."
            />

            <div className="container mx-auto px-4 py-12 md:py-16">
                <div className="max-w-3xl mx-auto space-y-12">
                    <div className="text-center">
                        <h1 className="text-3xl md:text-4xl font-bold mb-4">About blog.brajti.com</h1>
                        <p className="text-lg text-muted-foreground">
                            Your new home for discovering, discussing, and diving deep into the world of books.
                        </p>
                    </div>

                    <section className="space-y-4">
                        <p className="text-lg leading-relaxed">
                            In an age of endless digital noise, finding your next great read can be overwhelming. We believe in the power of the written word to transport, educate, and inspire.
                        </p>
                        <p className="text-lg leading-relaxed">
                            Our mission is to be your most trusted guide—a clear, reliable, and insightful voice that helps you connect with stories that matter.
                        </p>
                        <p className="text-base font-medium text-primary">
                            We're not just another algorithm. We are a destination for passionate readers who value quality, honesty, and thoughtful curation.
                        </p>
                    </section>

                    <hr className="border-border" />

                    <section className="space-y-4">
                        <h2 className="text-2xl font-bold">Who We Are</h2>
                        <div className="bg-muted/30 rounded-lg p-6 space-y-4">
                            <p>
                                <strong>blog.brajti.com</strong> is a content project by <strong>Vebotek</strong>, a digital media and technology company.
                            </p>
                            <p className="text-muted-foreground">
                                At Vebotek, we specialize in building high-quality online experiences. We are a team of technologists, editors, and content strategists who believe in using technology to foster community and share knowledge.
                            </p>
                            <p className="text-muted-foreground">
                                We launched this blog because, like you, we love books. We wanted to combine our passion for reading with our expertise in digital publishing to create a world-class resource for the reading community.
                            </p>
                        </div>
                    </section>

                    <section className="space-y-4">
                        <h2 className="text-2xl font-bold">Our Commitment to Quality</h2>
                        <p>
                            Your time is valuable, and so is the trust you place in our recommendations.
                        </p>
                        <p className="text-muted-foreground">
                            Our credibility is built on a foundation of professional integrity. While we are a company, our reviews and articles are driven by a genuine passion for literature, not by passing trends.
                        </p>

                        <div className="grid sm:grid-cols-3 gap-4 pt-4">
                            <div>
                                <h3 className="font-semibold mb-2">Professional Curation</h3>
                                <p className="text-sm text-muted-foreground">
                                    Our content is managed by a professional team, ensuring every article meets our high standards.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-semibold mb-2">Honest & Independent</h3>
                                <p className="text-sm text-muted-foreground">
                                    Our opinions are our own. We provide honest reviews to help you make informed decisions.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-semibold mb-2">A Broader View</h3>
                                <p className="text-sm text-muted-foreground">
                                    We explore diverse genres, champion international authors, and uncover hidden gems.
                                </p>
                            </div>
                        </div>
                    </section>

                    <section className="space-y-4">
                        <h2 className="text-2xl font-bold">For Publishers & Advertisers</h2>
                        <div className="border-l-4 border-primary pl-6 space-y-4">
                            <p>
                                <strong>blog.brajti.com</strong> offers a direct channel to a highly engaged and discerning audience of dedicated readers.
                            </p>
                            <p className="text-muted-foreground">
                                We value transparency and professionalism above all. We're open to partnerships that align with our brand values and provide genuine value to our readers.
                            </p>
                            <p className="text-sm italic text-muted-foreground">
                                For partnership inquiries, please reach out via our contact page.
                            </p>
                        </div>
                    </section>

                    <hr className="border-border" />

                    <section className="space-y-6">
                        <h2 className="text-2xl font-bold">Join the Conversation</h2>
                        <p>
                            This blog is more than just a website; it's a community. We invite you to join the discussion, share your perspectives, and become part of our story.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-6 pt-2">
                            <a
                                href="https://www.facebook.com/profile.php?id=61572166540243"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-primary hover:underline"
                            >
                                <Facebook className="w-5 h-5" />
                                <span className="font-medium">Follow us on Facebook</span>
                            </a>
                            <a
                                href="mailto:info@vebotek.com"
                                className="inline-flex items-center gap-2 text-primary hover:underline"
                            >
                                <Mail className="w-5 h-5" />
                                <span className="font-medium">info@vebotek.com</span>
                            </a>
                        </div>
                    </section>

                    <div className="text-center pt-8 border-t border-border">
                        <p className="text-muted-foreground italic">
                            Thank you for visiting. We can't wait to explore the next chapter with you.
                        </p>
                    </div>
                </div>
            </div>
        </PublicLayout>
    );
}
